import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { PRACTICE_PROXY_NOTICE, BOOK_USE_APPROXIMATION_NOTICE } from '@titrate/shared';
import { api, ApiRequestError } from '../lib/api';
import { keys, useBenchmark, useEntitlements } from '../lib/queries';
import { AccuracyBar, Callout, ErrorNotice, Markdown, Spinner, StatCard } from '../components/ui';

export function Results() {
  const { benchmarkId = '' } = useParams();
  const { data, isLoading, error } = useBenchmark(benchmarkId);
  const { data: ent } = useEntitlements();
  const qc = useQueryClient();

  const [generating, setGenerating] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);
  const [upgradeNeeded, setUpgradeNeeded] = useState(false);

  if (isLoading) return <Spinner label="Loading your results" />;
  if (error || !data) return <ErrorNotice message={error instanceof Error ? error.message : 'Not found.'} />;

  const b = data.benchmark;
  const paceDelta = b.avgTimeSec - b.benchmarkPaceSec;

  const generateReport = async () => {
    setGenerating(true);
    setReportError(null);
    setUpgradeNeeded(false);
    try {
      await api.post(`/api/benchmarks/${benchmarkId}/report`);
      await qc.invalidateQueries({ queryKey: keys.benchmark(benchmarkId) });
      await qc.invalidateQueries({ queryKey: keys.entitlements });
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setReportError(err.message);
        setUpgradeNeeded(err.isQuotaError);
      } else {
        setReportError('Could not generate your report.');
      }
    } finally {
      setGenerating(false);
    }
  };

  const remainingReports = ent?.entitlements.remaining.feedback_report ?? null;

  return (
    <div className="space-y-8">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-muted">
            {b.examCode} · {new Date(b.createdAt).toLocaleDateString('en-AU', { dateStyle: 'medium' })}
          </p>
          <h1 className="mt-1 text-3xl font-bold tracking-tight text-ink">Your results</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to={`/review/${b.sessionId}`} className="btn-secondary">Review every question</Link>
          <Link to="/dashboard" className="btn-ghost">Dashboard</Link>
        </div>
      </header>

      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Weighted accuracy"
          value={b.accuracyScore}
          suffix="%"
          tone={b.accuracyScore >= 70 ? 'good' : b.accuracyScore >= 50 ? 'warn' : 'bad'}
          hint="Weighted by each domain's share of the real paper."
        />
        <StatCard
          label="Pace score"
          value={b.speedScore}
          suffix="/100"
          tone={b.speedScore >= 90 ? 'good' : b.speedScore >= 70 ? 'warn' : 'bad'}
          hint={`${b.avgTimeSec}s per question against the exam's ${b.benchmarkPaceSec}s.`}
        />
        <StatCard
          label="Pace margin"
          value={paceDelta <= 0 ? `${Math.abs(paceDelta)}s` : `+${paceDelta}s`}
          tone={paceDelta <= 0 ? 'good' : 'bad'}
          hint={paceDelta <= 0 ? 'Inside the time you have.' : 'Slower than the paper allows.'}
        />
        {b.bookRelianceScore !== null ? (
          <StatCard
            label="Reference reliance"
            value={b.bookRelianceScore}
            suffix="/100"
            tone={b.bookRelianceScore > 50 ? 'bad' : b.bookRelianceScore > 25 ? 'warn' : 'good'}
            hint="Self-reported lookups blended with slow-response outliers."
          />
        ) : (
          <StatCard label="Format" value="Closed-book" hint="No reference use is tracked for this exam." />
        )}
      </section>

      <Callout>{PRACTICE_PROXY_NOTICE}</Callout>

      <section className="card p-4 sm:p-6">
        <h2 className="text-lg font-semibold text-ink">Accuracy by content domain</h2>
        <p className="mt-1 text-sm text-muted">
          Compare your accuracy against how much of the real paper each domain represents. A gap in a
          heavily weighted domain costs far more than the same gap in a light one.
        </p>
        <ul className="mt-5 space-y-4">
          {b.domainBreakdown.map((row) => (
            <li key={row.domainCode}>
              <div className="mb-1.5 flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-sm font-medium text-ink">
                  <span className="text-muted">{row.domainCode}</span> {row.domainName}
                </span>
                <span className="text-xs text-muted">
                  {row.attempted > 0 ? `${row.correct}/${row.attempted} correct · ${row.avgTimeSec}s avg` : 'Not assessed'}
                  {row.bookReliancePct !== null && row.attempted > 0 ? ` · books on ${row.bookReliancePct}%` : ''}
                </span>
              </div>
              <AccuracyBar pct={row.accuracyPct} weightPct={row.weightPct} />
            </li>
          ))}
        </ul>
        {b.bookRelianceScore !== null ? (
          <p className="mt-5 border-t border-line pt-4 text-xs leading-relaxed text-muted">
            {BOOK_USE_APPROXIMATION_NOTICE}
          </p>
        ) : null}
      </section>

      {b.tagBreakdown.some((t) => t.tagType === 'cognitive_level') ? (
        <section className="card p-4 sm:p-6">
          <h2 className="text-lg font-semibold text-ink">Cognitive level</h2>
          <p className="mt-1 text-sm text-muted">
            Where your marks are coming from. Strong recall with weak application is a very different
            problem from the reverse — and needs very different revision.
          </p>
          <ul className="mt-5 space-y-4">
            {b.tagBreakdown
              .filter((t) => t.tagType === 'cognitive_level')
              .map((t) => (
                <li key={t.tagName}>
                  <div className="mb-1.5 flex items-baseline justify-between gap-2">
                    <span className="text-sm font-medium text-ink">{t.tagName}</span>
                    <span className="text-xs text-muted">{t.correct}/{t.attempted}</span>
                  </div>
                  <AccuracyBar pct={t.accuracyPct} />
                </li>
              ))}
          </ul>
        </section>
      ) : null}

      <section className="card p-4 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-ink">AI feedback report</h2>
            <p className="mt-1 max-w-2xl text-sm text-muted">
              A written analysis of your gaps, your pace and what to do next — specific to these
              results, not a generic template.
            </p>
          </div>
          {!b.reportText ? (
            <button type="button" className="btn-primary w-full shrink-0 sm:w-auto" onClick={() => void generateReport()} disabled={generating}>
              {generating ? 'Writing your report…' : 'Generate report'}
            </button>
          ) : null}
        </div>

        {remainingReports !== null && !b.reportText ? (
          <p className="mt-2.5 text-xs text-muted">
            {remainingReports} report{remainingReports === 1 ? '' : 's'} left this billing period.
          </p>
        ) : null}

        {reportError ? (
          <div className="mt-4">
            <ErrorNotice title={upgradeNeeded ? 'Upgrade required' : 'Report failed'} message={reportError} />
            {upgradeNeeded ? (
              <Link to="/pricing" className="btn-primary mt-3">See plans</Link>
            ) : null}
          </div>
        ) : null}

        {b.reportText ? (
          <div className="mt-5 border-t border-line pt-5">
            <Markdown content={b.reportText} />
          </div>
        ) : null}
      </section>

      <section className="card p-4 sm:p-6">
        <h2 className="text-lg font-semibold text-ink">Next steps</h2>
        <div className="mt-4 flex flex-wrap gap-3">
          <Link to="/revision" className="btn-primary">Build my revision timetable</Link>
          <Link to="/practice" className="btn-secondary">Practise my weakest domain</Link>
          <Link to={`/review/${b.sessionId}`} className="btn-secondary">Review my answers</Link>
        </div>
      </section>
    </div>
  );
}
